import React from 'react';
import { CalendarDays, Clock, MapPin, Award, Users } from 'lucide-react';
import { FONT, MONO } from './ui';

const BARS = Array.from({ length: 64 }, (_, i) => 0.25 + 0.75 * Math.abs(Math.sin(i * 0.43) * Math.cos(i * 0.17)));

export const Hero = ({ d, L, t, portrait }) => (
  <header className="relative overflow-hidden bg-[#040919] text-white pt-28" data-testid="lecture-hero">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-blue-600/30 blur-[140px]" />
      <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-16 grid lg:grid-cols-[1.35fr_1fr] gap-12 items-end">
      <div className="space-y-7">
        <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em]" style={MONO}>
          <span className="px-3 py-1 rounded-full border border-blue-400/40 text-blue-200">Lecture</span>
          <span className="px-3 py-1 rounded-full border border-white/15 text-white/70">CLA Insubria × LM STEC Varese</span>
        </div>
        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[0.92]" style={FONT} data-testid="hero-title">
          The Sound<br /><span className="bg-gradient-to-r from-blue-400 via-blue-300 to-cyan-200 bg-clip-text text-transparent">of Authority</span>
        </h1>
        <p className="text-lg md:text-xl text-blue-100/85 max-w-2xl font-medium" data-testid="hero-subtitle">{L(d.subtitle)}</p>
        <p className="text-sm uppercase tracking-[0.3em] text-blue-300" style={MONO}>{L(d.tagline)}</p>
        <div className="flex items-end gap-[3px] h-16" aria-hidden="true">
          {BARS.map((h, i) => (
            <span key={i} className="flex-1 rounded-full bg-gradient-to-t from-blue-600 to-cyan-300 origin-bottom" style={{ height: `${h * 100}%`, animation: `vfEq 1.6s ease-in-out ${(i % 16) * 0.08}s infinite alternate` }} />
          ))}
        </div>
      </div>
      <div className="relative">
        <div className="relative rounded-[2rem] overflow-hidden border border-white/15 shadow-2xl shadow-blue-900/50">
          <img src={portrait} alt="Prof. Steve Dapper" className="w-full h-[520px] object-cover object-[50%_75%]" data-testid="hero-portrait" />
          <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#040919] via-[#040919]/70 to-transparent">
            <p className="text-2xl font-extrabold" style={FONT}>{d.speaker.name}</p>
            {L(d.speaker.roles).map((r) => <p key={r} className="text-xs text-blue-100/80">{r}</p>)}
          </div>
        </div>
        <span className="absolute -top-4 -right-3 rotate-3 px-4 py-2 rounded-xl bg-amber-400 text-slate-950 text-xs font-extrabold uppercase tracking-widest shadow-lg" data-testid="hero-seats-status">{L(d.meta.seats)}</span>
      </div>
    </div>
    <div className="relative border-t border-white/10 bg-white/[0.03] backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[[CalendarDays, t.date, L(d.meta.date), 'hero-event-date-badge'], [Clock, t.time, `${d.meta.time} · ${L(d.meta.duration)}`, 'hero-event-time'], [MapPin, t.venue, L(d.meta.venue), 'hero-event-venue'], [Award, t.credits, '0,5 · SCOM / STEC', 'hero-event-credits']].map(([I, k, v, id]) => (
          <div key={id} className="flex items-start gap-3" data-testid={id}>
            <span className="w-10 h-10 shrink-0 rounded-xl bg-blue-600/25 border border-blue-400/30 flex items-center justify-center"><I className="w-5 h-5 text-blue-200" /></span>
            <div><p className="text-[10px] uppercase tracking-[0.2em] text-blue-300/80" style={MONO}>{k}</p><p className="text-sm font-semibold">{v}</p></div>
          </div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-blue-100/60">
        <Users className="w-4 h-4" />{d.meta.hosts.map((h) => <span key={h}>{h}</span>)}
      </div>
    </div>
  </header>
);
